import { TableContainer, 
        TableHead, 
        TableBody, 
        TableCell, 
        TableRow, 
        Paper,
        Table,
        Accordion,
        AccordionSummary,
        AccordionDetails,
        List,
        ListItem,
        Grid
    } from "@mui/material";

import MDTypography from "../../../components/MDTypography";
import MDBox from "../../../components/MDBox";
import PercentageBar from "../../../components/PercentageBar";
import { useState } from "react";
import { ExpandMore } from "@mui/icons-material";

function BreakoutTab({assetSpecs, assetDescription, assetCapabilities, scoreData, satisfiedReq, unsatisfiedReq}) {
    const [assetDescriptionExpanded, setAssetDescriptionExpanded] = useState(true);
    const [assetSpecsExpanded, setAssetSpecsExpanded] = useState(true);
    const [scoreDataExpanded, setScoreDataExpanded] = useState(false);
    const [assetCapabilitiesExpanded, setAssetCapabilitiesExpanded] = useState(false);
    const [satReqExpanded, setSatReqExpanded] = useState(false);
    const [unsatReqExpanded, setUnsatReqExpanded] = useState(false);

    return (
        <MDBox px={1} sx={{width:"100%"}}>
            {/* Asset Description */}
            <Paper sx={{my: 1}}>
                <Accordion expanded={assetDescriptionExpanded} onChange={() => setAssetDescriptionExpanded(!assetDescriptionExpanded)}>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <MDTypography variant="h5">Description</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                    <MDTypography variant="body2" sx={{ color: "dark_teal.main", whiteSpace: 'pre-line' }}>
                        {assetDescription}
                    </MDTypography>
                    </AccordionDetails>
                </Accordion>
            </Paper>

            {/* Asset Specs */}
            <Paper sx={{my: 1}}>               
                <Accordion expanded={assetSpecsExpanded} onChange={() => setAssetSpecsExpanded(!assetSpecsExpanded)}>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <MDTypography variant="h5">Specs</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <MDBox ml={2} mr={1} display="flex" flexDirection="columns">
                            <List sx={{listStyleType: "disc"}}>
                                {assetSpecs.map((spec, specIndex) => (
                                    <ListItem key={`spec-${specIndex}`} sx={{ display: 'block', mb: 2 }}>
                                        <MDTypography component="div" variant="body2" >
                                            <MDBox fontWeight="bold" display="inline">
                                                {spec.header}:
                                            </MDBox>
                                        </MDTypography>
                                        <MDBox pl={2} mt={1}>
                                            {Array.isArray(spec.description) ? (
                                                spec.description.map((descItem, descIndex) => (
                                                    <MDTypography 
                                                        key={`desc-${specIndex}-${descIndex}`}
                                                        component="div" 
                                                        variant="body2"
                                                        sx={{ mb: 0.5, color: "dark_teal.main" }}
                                                    >
                                                        • {descItem}
                                                    </MDTypography>
                                                ))
                                            ) : (
                                                <MDTypography component="div" variant="body2" sx={{color: "dark_teal.main"}}>
                                                    {spec.description}
                                                </MDTypography>
                                            )}
                                        </MDBox>
                                    </ListItem>
                                ))}
                            </List>
                        </MDBox>
                    </AccordionDetails>
                </Accordion>
            </Paper>

            {/* Bundle Scoring Table */}
            <Paper sx={{my: 1}}>
                <Accordion expanded={scoreDataExpanded} onChange={() => setScoreDataExpanded(!scoreDataExpanded)}>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <MDTypography variant="h5">Asset Scoring</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow>
                                        <TableCell sx={{color: "text.main"}}>Cost</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Timeline</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Risk</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Cost&nbsp;Score</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Time&nbsp;Score</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Risk&nbsp;Score</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Overall&nbsp;Score</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scoreData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{row.cost}</TableCell>
                                            <TableCell>{row.timeline}</TableCell>
                                            <TableCell>{row.risk}</TableCell>
                                            <TableCell>{row.costScore}</TableCell>
                                            <TableCell>{row.timeScore}</TableCell>
                                            <TableCell>{row.riskScore}</TableCell>
                                            <TableCell>{row.overallScore}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            </Paper>
            
            {/* Asset Capabilities */}
            <Paper sx={{my: 1}}>
                <Accordion expanded={assetCapabilitiesExpanded} onChange={() => setAssetCapabilitiesExpanded(!assetCapabilitiesExpanded)}>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <MDTypography variant="h5">Capability Scores</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container>
                            {assetCapabilities.map((item) => (
                                <Grid item lg={12} mb={1}>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore/>}>
                                            <Grid container>
                                                <Grid item lg={12}>
                                                    <MDTypography fontSize={16}>{item.main}</MDTypography>
                                                </Grid>
                                                <Grid item xs={12} lg={12}>
                                                    <PercentageBar value={item.value}/>
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.subset.map((subItem) => (
                                                <Grid container sx={{width: "90%", margin: "0 auto"}}>
                                                    <Grid item lg={12}>
                                                        <MDTypography fontSize={16}>{subItem.name}</MDTypography>
                                                    </Grid>
                                                    <Grid item xs={12} lg={12}>
                                                        <PercentageBar value={subItem.value} color="#64D5CF"/>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            ))}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Paper>

            {/* Satisfied Requirements Table */}
            <Paper sx={{my: 1}}>
                <Accordion expanded={satReqExpanded} onChange={() => setSatReqExpanded(!satReqExpanded)}>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <MDTypography variant="h5">Satisfied Requirements</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer sx={{maxHeight: 300}}>
                            <Table stickyHeader>
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow>
                                        <TableCell sx={{color: "text.main"}}>Title</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {satisfiedReq.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{row.title}</TableCell>
                                            <TableCell>{row.description}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            </Paper>
            
            {/* Unsatisfied Requirements Table */}
            <Paper sx={{my: 1}}>
                <Accordion expanded={unsatReqExpanded} onChange={() => setUnsatReqExpanded(!unsatReqExpanded)}>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <MDTypography variant="h5">Unsatisfied Requirements</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer sx={{maxHeight: 300}}>
                            <Table stickyHeader>
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow>
                                        <TableCell sx={{color: "text.main"}}>Title</TableCell>
                                        <TableCell sx={{color: "text.main"}}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {unsatisfiedReq.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{row.title}</TableCell>
                                            <TableCell>{row.description}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            </Paper>
        </MDBox>
    )
}

export default BreakoutTab;